import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'
import { Helmet } from 'react-helmet-async'
import { fetchLatestNews } from '../api/news'
import ClassicAppBar from '../components/ClassicAppBar'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store/store'
import { setLanguage } from '../store/slices/languageSlice'
import { toggleMode } from '../store/slices/themeSlice'
import i18n from '../i18n/i18n'

const NewsPage: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const themeMode = useSelector((state: RootState) => state.theme.mode)
  const [items, setItems] = React.useState<
    Array<{ id: number; title: string; summary?: string; imageUrl?: string }>
  >([])

  React.useEffect(() => {
    fetchLatestNews(12).then(setItems)
  }, [])

  const handleLanguageChange = (lang: 'en' | 'ta' | 'de') => {
    i18n.changeLanguage(lang)
    dispatch(setLanguage(lang))
  }

  const handleToggleMode = () => {
    dispatch(toggleMode())
  }

  return (
    <>
      <Helmet>
        <title>{t('site.latest')} - YarlHub</title>
        <meta name="description" content="Stay updated with the latest news and events from the Tamil community" />
      </Helmet>

      <div className="min-h-screen flex flex-column">
        <ClassicAppBar
          mode={themeMode}
          onToggleMode={handleToggleMode}
          onChangeLang={handleLanguageChange}
        />

        {/* Hero Section */}
        <div
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1a252f 0%, #2c3e50 50%, #1a252f 100%)',
            paddingTop: '4rem',
            paddingBottom: '4rem'
          }}
        >
          <div className="absolute inset-0 opacity-10">
            <div style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(211, 84, 0, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(211, 84, 0, 0.2) 0%, transparent 50%)',
              width: '100%',
              height: '100%'
            }}></div>
          </div>

          <div className="container mx-auto px-4 relative z-1">
            <div className="text-center">
              <div
                className="inline-flex align-items-center justify-content-center mb-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(211, 84, 0, 0.2), rgba(230, 126, 34, 0.2))',
                  padding: '1rem 2rem',
                  borderRadius: '50px',
                  border: '2px solid rgba(211, 84, 0, 0.3)'
                }}
              >
                <i className="pi pi-align-left text-orange-500 text-3xl mr-3"></i>
                <span className="text-white font-bold text-xl">{t('site.latest')}</span>
              </div>

              <h1
                className="text-white font-bold mb-4"
                style={{
                  fontSize: 'clamp(3rem, 6vw, 5rem)',
                  fontFamily: 'serif',
                  lineHeight: '1.2',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                }}
              >
                Latest Tamil News
              </h1>

              <p
                className="text-white-alpha-90 mx-auto text-xl line-height-3"
                style={{ maxWidth: '700px' }}
              >
                Stay connected with breaking news, community updates, and cultural events from around the world
              </p>
            </div>
          </div>
        </div>

        {/* News Content */}
        <div className="py-8 relative surface-ground">
          <div className="container mx-auto px-4" style={{ maxWidth: '1400px' }}>
            {items.length === 0 ? (
              <div className="flex justify-content-center">
                <div
                  className="p-6 border-round-3xl text-center"
                  style={{
                    maxWidth: '600px',
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
                    backdropFilter: 'blur(10px)',
                    border: '2px dashed rgba(211, 84, 0, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <i className="pi pi-inbox text-6xl mb-4" style={{ color: '#d35400', opacity: 0.6 }}></i>
                  <h3 className="text-2xl font-bold text-900 mb-3">No News Available</h3>
                  <p className="text-600 line-height-3 mb-4">
                    We couldn't load any news at the moment. The backend service might be unavailable.
                  </p>
                  <Button
                    label="Refresh Page"
                    icon="pi pi-refresh"
                    className="border-round-3xl"
                    style={{
                      background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                      border: 'none'
                    }}
                    onClick={() => window.location.reload()}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Featured Article - First Item Large */}
                {items[0] && (
                  <div className="mb-6">
                    <div
                      className="border-round-3xl overflow-hidden surface-card relative"
                      style={{
                        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(211, 84, 0, 0.2)',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.5s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-8px)'
                        e.currentTarget.style.boxShadow = '0 30px 80px rgba(211, 84, 0, 0.3)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      <div className="grid grid-nogutter">
                        <div className="col-12 lg:col-7">
                          <div className="relative overflow-hidden" style={{ height: '100%', minHeight: '400px' }}>
                            <img
                              src={items[0].imageUrl || `https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200&h=800&fit=crop`}
                              alt={items[0].title}
                              className="w-full h-full object-cover"
                              style={{ transition: 'transform 0.6s ease' }}
                              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)' }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
                            />
                            <div className="absolute top-4 left-4">
                              <Chip
                                label="Featured"
                                className="font-bold shadow-6"
                                style={{
                                  background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                                  color: 'white',
                                  padding: '0.75rem 1.5rem',
                                  fontSize: '1rem'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-12 lg:col-5 p-6 flex flex-column justify-content-center">
                          <h2
                            className="text-4xl font-bold text-900 mb-4 line-height-3"
                            style={{
                              fontFamily: 'serif',
                              background: 'linear-gradient(135deg, #1a252f 0%, #d35400 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}
                          >
                            {items[0].title}
                          </h2>

                          <p className="text-700 line-height-3 mb-4 text-lg">
                            {items[0].summary || 'Discover the latest developments and stories that matter to our community...'}
                          </p>

                          <div className="flex align-items-center gap-4 mb-4">
                            <div className="flex align-items-center gap-2">
                              <i className="pi pi-clock text-orange-500"></i>
                              <span className="text-sm text-600 font-semibold">2 hours ago</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                              <i className="pi pi-eye text-orange-500"></i>
                              <span className="text-sm text-600 font-semibold">2.5K views</span>
                            </div>
                          </div>

                          <Button
                            label="Read Full Story"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            className="border-round-3xl font-bold p-3"
                            style={{
                              background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                              border: 'none',
                              fontSize: '1.1rem'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Regular News Grid */}
                <div className="grid">
                  {items.slice(1).map((item) => (
                    <div key={item.id} className="col-12 md:col-6 lg:col-4 p-3">
                      <div
                        className="h-full border-round-3xl overflow-hidden surface-card relative"
                        style={{
                          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(211, 84, 0, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.4s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)'
                          e.currentTarget.style.boxShadow = '0 24px 48px rgba(211, 84, 0, 0.25)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)'
                          e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <div className="relative overflow-hidden" style={{ height: '280px' }}>
                          <img
                            src={item.imageUrl || `https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&h=600&fit=crop`}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            style={{ transition: 'transform 0.6s ease' }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)' }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
                          />
                          <div
                            className="absolute bottom-0 left-0 w-full h-full"
                            style={{
                              background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 60%)'
                            }}
                          />
                          <div className="absolute top-3 right-3">
                            <Chip
                              label="Latest"
                              className="font-bold shadow-4"
                              style={{
                                background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                                color: 'white'
                              }}
                            />
                          </div>
                        </div>

                        <div className="p-4">
                          <h3
                            className="text-2xl font-bold text-900 mb-3 line-height-3"
                            style={{
                              fontFamily: 'serif',
                              background: 'linear-gradient(135deg, #1a252f 0%, #d35400 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              minHeight: '70px'
                            }}
                          >
                            {item.title}
                          </h3>

                          <p className="text-600 line-height-3 mb-4" style={{ minHeight: '60px' }}>
                            {item.summary || 'Read more about this important news story...'}
                          </p>

                          <div className="flex align-items-center gap-3 mb-4">
                            <div className="flex align-items-center gap-2">
                              <i className="pi pi-clock text-orange-500 text-sm"></i>
                              <span className="text-xs text-600 font-semibold">3 hours ago</span>
                            </div>
                            <div className="flex align-items-center gap-2">
                              <i className="pi pi-eye text-orange-500 text-sm"></i>
                              <span className="text-xs text-600 font-semibold">1.2K views</span>
                            </div>
                          </div>

                          <Button
                            label="Read More"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            className="w-full border-round-3xl font-bold"
                            style={{
                              background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
                              border: 'none',
                              padding: '0.75rem'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <footer
          className="py-6 mt-auto"
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            borderTop: '1px solid rgba(211, 84, 0, 0.2)'
          }}
        >
          <div className="container mx-auto px-4 text-center">
            <p className="text-white-alpha-90 text-sm font-semibold">
              © 2024 YarlHub. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  )
}

export default NewsPage
